import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';

import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { SpectrumAdvisorInfoComponent } from '../spectrum-advisor-info/spectrum-advisor-info.component';
import { IMaskModule } from 'angular-imask';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';
import { AccessConstants } from '../../../../core/constants/access-constants';
import { IMaskConstants } from '../../../../core/constants/imask';
import { NgxPhoneConstants } from '../../../../core/constants/ngx';
import { RouteConstants } from '../../../../core/constants/routes';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { Contact } from '../../../../core/models/civi-crm/contact/contact';
import { Organization } from '../../../../core/models/civi-crm/organization';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { AuthFlowApiService } from '../../../../core/services/auth-wrapper/auth-flow-api.service';
import { PhoneNumberService } from '../../../../core/services/phone-number/phone-number.service';
import { PostalCodeService } from '../../../../core/services/postal-code/postal-code.service';
import { StateProvince } from '../../../../core/services/resources/models/state-province';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { CardsOnFileComponent } from '../cards-on-file/cards-on-file.component';
import { UserStateService } from '../../shared/services/user-state/user-state.service';
import { UserProfileService } from '../shared/services/user-profile/user-profile.service';
import { ExecutorProfile } from './models/executor-profile';
import { UserProfileExecutorRequest } from './models/user-profile-executor-request';
import { UserAvailabilityComponent } from '../user-availability/user-availability.component';

@Component({
    selector: 'app-edit-profile',
    imports: [
        RouterModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        NgxMaskDirective,
        SpectrumAdvisorInfoComponent,
        IMaskModule,
        CardsOnFileComponent,
        UserAvailabilityComponent,
    ],
    templateUrl: './edit-executor-profile.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [provideNgxMask()],
})
export class EditExecutorProfileComponent
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    private readonly resourcesService = inject(ResourcesService);
    private readonly userProfileService = inject(UserProfileService);
    private readonly userStateService = inject(UserStateService);
    private readonly phoneNumberService = inject(PhoneNumberService);
    private readonly postalCodeService = inject(PostalCodeService);
    private readonly authApiService = inject(AuthFlowApiService);
    private readonly router = inject(Router);

    maskOptions = IMaskConstants;
    phoneConstants = NgxPhoneConstants;
    regExPattern = RegExPattern;

    hasChanges = false;
    passwordResetTriggered = false;

    stateProvinces: StateProvince[] = [];
    organization: Organization | null = null;
    advisorContact: Contact | null = null;

    form: FormGroup<FormGroupType<ExecutorProfile>> = new FormGroup<
        FormGroupType<ExecutorProfile>
    >({
        streetAddress: new FormControl('', [Validators.required]),
        city: new FormControl('', [Validators.required]),
        stateProvinceId: new FormControl(undefined!, [Validators.required]),
        postalCode: new FormControl('', [Validators.required]),
        jobTitle: new FormControl('', [Validators.required]),
        phoneNumber: new FormControl('', [Validators.required]),
    });
    cancelRoute = RouteConstants.Profile.getFullRoute('INDEX');

    get canAccessPayments(): boolean {
        const role = this.userStateService.getRole();
        return (
            role !== undefined &&
            AccessConstants.RolesThatCanAccessPayments.includes(role)
        );
    }

    get canManageAvailability(): boolean {
        const role = this.userStateService.getRole();
        return (
            role !== undefined &&
            AccessConstants.RolesThatCanManageUserAvailability.includes(role)
        );
    }

    get userId(): number | undefined {
        return this.userStateService.getUser()?.id;
    }

    private destroy$ = new Subject<void>();

    async ngOnInit() {
        const [stateProvinces, profileResponse] = await Promise.all([
            this.getStateProvinces(),
            this.userProfileService.get(),
        ]);

        this.stateProvinces = stateProvinces;
        this.organization = profileResponse.organization;
        this.advisorContact = profileResponse.advisorContact;

        this.form.setValue({
            streetAddress: profileResponse.contact.address.streetAddress,
            city: profileResponse.contact.address.city,
            stateProvinceId:
                profileResponse.contact.address.stateProvince.toString(),
            postalCode: this.postalCodeService.buildPostalCodeString(
                profileResponse.contact.address.postalCode,
                profileResponse.contact.address.postalCodeSuffix,
            ),
            jobTitle: profileResponse.contact.jobTitle,
            phoneNumber: this.phoneNumberService.buildPhoneNumberForInput(
                profileResponse.contact.phone.phoneNumber,
                profileResponse.contact.phone.phoneExtension,
            ),
        });

        // ngx-mask dirties the form when writing into masked controls; restore pristine so
        // loading the profile isn't treated as a user edit.
        this.form.markAsPristine();

        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.hasChanges = this.form.dirty;
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Fetches the list of state provinces from the server and sets the state provinces array.
     */
    private getStateProvinces(): Promise<StateProvince[]> {
        return lastValueFrom(this.resourcesService.getStateProvinces());
    }

    submit() {
        this.hasChanges = false;

        const { phoneNumber, phoneNumberExtension } =
            this.phoneNumberService.getPhoneNumberAndExtension(
                this.form.value.phoneNumber!,
            );

        const returnTo = RouteConstants.Profile.getFullRoute('INDEX');
        if (!this.form.touched) {
            this.router.navigate([returnTo]);
        }

        const request: UserProfileExecutorRequest = {
            streetAddress: this.form.controls.streetAddress.value,
            city: this.form.controls.city.value,
            stateProvinceId: this.form.controls.stateProvinceId.value,
            postalCode: this.form.controls.postalCode.value,
            jobTitle: this.form.controls.jobTitle.value,
            phoneNumber: phoneNumber,
            phoneNumberExtension: phoneNumberExtension,
        };

        this.userProfileService
            .update(request)
            .then(() => {
                // TODO: show success popup
                this.router.navigate([returnTo]);
            })
            .catch((error) => {
                console.error('Error updating profile:', error);
            });
    }

    triggerPasswordReset() {
        this.passwordResetTriggered = true;

        this.authApiService.triggerPasswordReset();
    }
}
